// Developer: Ankit Gupta
import { useAuth } from './useAuthHook';

// ─── Role constants ───────────────────────────────────────────────────────────

export const ROLES = {
  ADMIN: 'datapilot_admin',
  PROJECT_ADMIN: 'datapilot_project_admin',
  MEMBER: 'datapilot_user',
  VIEWER: 'datapilot_viewer',
} as const;

export type AppRole = (typeof ROLES)[keyof typeof ROLES];

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface PermissionsValue {
  /** Check whether the current user has a specific role. */
  hasRole: (role: string) => boolean;
  /** True when the user holds the global admin role. */
  isAdmin: boolean;
  /** True when the user holds at least the project-admin role. */
  isProjectAdmin: boolean;
  /**
   * Returns true when the user can access a given project.
   * Admins can access any project; other users must be an explicit member
   * (the API layer enforces the actual membership check – this is a
   * client-side shortcut for hiding/showing UI controls).
   */
  canAccessProject: (projectId?: number) => boolean;
  /** True when the user is at least a regular member (not just a viewer). */
  isMember: boolean;
}

export function usePermissions(): PermissionsValue {
  const { roles } = useAuth();

  const hasRole = (role: string) => roles.includes(role);

  const isAdmin = hasRole(ROLES.ADMIN);
  const isProjectAdmin = isAdmin || hasRole(ROLES.PROJECT_ADMIN);
  const isMember =
    isAdmin ||
    isProjectAdmin ||
    hasRole(ROLES.MEMBER);

  const canAccessProject = (_projectId?: number): boolean => {
    // Admins can always access every project.
    // For non-admins the real check is performed by the backend; we return
    // true here so the router doesn't block navigation, and let the API
    // 403 surface as an error state.
    return isAdmin || isMember;
  };

  return {
    hasRole,
    isAdmin,
    isProjectAdmin,
    canAccessProject,
    isMember,
  };
}
